/**
 * A navegação da sidebar — transcrita do protótipo (`telas/dv-shell.js` + o markup
 * das 49 telas).
 *
 * Os rótulos vêm do GLOSSARIO e são literais: "Basquete (Pedidos)" é o nome oficial
 * da área, "Recebíveis & Cobrança" leva o "&" — nenhuma tela inventa sinônimo. O
 * ícone de cada item é o mesmo do protótipo, porque a baseline visual compara pixel.
 *
 * O `modulo` de cada item é a chave EXATA de `nivel_permissao.modulo` (spec 02 §1.8).
 * É por ele que o shell vai esconder o que a pessoa não pode ver — na origem, não na
 * tela (DS-28). Enquanto a sessão não existe (passo 3.8), a lista aparece inteira.
 */
import type { Modulo } from '@dv/domain'

export interface ItemDeNavegacao {
  readonly rotulo: string
  readonly rota: string
  readonly icone: string
  readonly modulo: Modulo
}

export interface GrupoDeNavegacao {
  readonly chave: string
  readonly rotulo: string
  readonly icone: string
  readonly itens: readonly ItemDeNavegacao[]
}

export type EntradaDeNavegacao = ItemDeNavegacao | GrupoDeNavegacao

export function ehGrupo(entrada: EntradaDeNavegacao): entrada is GrupoDeNavegacao {
  return 'itens' in entrada
}

/** Grupo que deve acompanhar a rota; páginas-raiz deixam todos os accordions fechados. */
export function grupoDaRota(rota: string): string | null {
  for (const entrada of NAVEGACAO) {
    if (!ehGrupo(entrada)) continue
    if (entrada.itens.some((item) => rota === item.rota || rota.startsWith(`${item.rota}/`))) return entrada.chave
  }
  // Duas páginas financeiras são destinos internos, sem item próprio na sidebar.
  if (rota === '/variancia' || rota === '/rentabilidade') return 'fin'
  return null
}

export const NAVEGACAO: readonly EntradaDeNavegacao[] = [
  { rotulo: 'Visão Geral', rota: '/', icone: 'layout-dashboard', modulo: 'dashboard' },
  {
    chave: 'comercial',
    rotulo: 'Comercial',
    icone: 'target',
    itens: [
      { rotulo: 'Empresas', rota: '/empresas', icone: 'building-2', modulo: 'crm' },
      { rotulo: 'Contatos', rota: '/contatos', icone: 'users', modulo: 'crm' },
      { rotulo: 'Negócios', rota: '/negocios', icone: 'target', modulo: 'negocios' },
      { rotulo: 'Metas', rota: '/metas', icone: 'goal', modulo: 'metas_previsao' },
      { rotulo: 'Previsão', rota: '/previsao', icone: 'trending-up', modulo: 'metas_previsao' },
    ],
  },
  {
    chave: 'receita',
    rotulo: 'Receita',
    icone: 'receipt',
    itens: [
      { rotulo: 'Basquete (Pedidos)', rota: '/pedidos', icone: 'receipt', modulo: 'pedidos' },
      { rotulo: 'Faturamento & NFs', rota: '/faturamento', icone: 'file-text', modulo: 'faturamento' },
      { rotulo: 'Recebíveis & Cobrança', rota: '/recebiveis', icone: 'hand-coins', modulo: 'recebiveis' },
      { rotulo: 'Comissões', rota: '/comissoes', icone: 'handshake', modulo: 'comissoes' },
    ],
  },
  {
    chave: 'ops',
    rotulo: 'Operações de Mídia',
    icone: 'radio-tower',
    itens: [{ rotulo: 'Campanhas', rota: '/campanhas', icone: 'radio-tower', modulo: 'midia' }],
  },
  {
    chave: 'fin',
    rotulo: 'Financeiro',
    icone: 'wallet',
    itens: [
      { rotulo: 'Fluxo de Caixa', rota: '/fluxo-de-caixa', icone: 'wallet', modulo: 'tesouraria' },
      { rotulo: 'Contas Bancárias', rota: '/contas-bancarias', icone: 'landmark', modulo: 'tesouraria' },
      { rotulo: 'Conciliação', rota: '/conciliacao', icone: 'arrow-left-right', modulo: 'conciliacao' },
      { rotulo: 'Contas a Pagar', rota: '/contas-a-pagar', icone: 'file-minus', modulo: 'contas_a_pagar' },
      // DS-615: a Central de Tributos não existe nas 49 telas; o pedido de 01/10 (§17) a põe no Financeiro.
      { rotulo: 'Tributos', rota: '/tributos', icone: 'receipt', modulo: 'tributos' },
      { rotulo: 'Aprovações', rota: '/aprovacoes', icone: 'stamp', modulo: 'aprovacao_desembolso' },
      { rotulo: 'Fornecedores', rota: '/fornecedores', icone: 'truck', modulo: 'fornecedores' },
      { rotulo: 'Orçamento', rota: '/orcamento', icone: 'calculator', modulo: 'orcamento' },
      { rotulo: 'Plano de Contas', rota: '/plano-de-contas', icone: 'list-tree', modulo: 'contabil_dre' },
      { rotulo: 'Ativos intangíveis', rota: '/ativos-intangiveis', icone: 'package', modulo: 'contabil_dre' },
      { rotulo: 'DRE & Demonstrativos', rota: '/dre', icone: 'chart-column', modulo: 'contabil_dre' },
      // "Rentabilidade por Cliente" (tela 8.6) NÃO entra no menu: ela existe no mapa
      // de destinos do `dv-shell.js`, mas não está na sidebar de nenhuma das 49
      // telas — e a tela é o contrato. Chega-se a ela por link de outra página.
      { rotulo: 'Relatórios', rota: '/relatorios', icone: 'files', modulo: 'relatorios' },
    ],
  },
  { rotulo: 'RH', rota: '/rh', icone: 'user-round', modulo: 'rh' },
  { rotulo: 'Tarefas', rota: '/tarefas', icone: 'square-check-big', modulo: 'tarefas' },
  { rotulo: 'Configurações', rota: '/configuracoes', icone: 'settings', modulo: 'catalogos' },
]
